import {api} from "@core/api";
import {Selectors} from "@core/store/selectors";
import {Person} from "@mui/icons-material";
import {
  Avatar,
  Stack,
  SxProps,
  Theme,
  Tooltip,
  Typography,
} from "@mui/material";
import {TypographyVariant} from "@mui/material/styles/createTypography";
import {skipToken} from "@reduxjs/toolkit/query";
import {useThemeContext} from "@theme";
import {acronym, getUserAvatarColor, truncateSx} from "@util";
import {getUserText} from "@util";
import {MouseEvent} from "react";
import {useSelector} from "react-redux";

const UserAvatar = ({
  id,
  full,
  width,
  sx,
  variant,
  emptyLabel,
  onClick,
}: {
  id: string | undefined | null;
  full?: boolean;
  width?: number;
  sx?: SxProps<Theme>;
  variant?: TypographyVariant;
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
  emptyLabel?: string;
}) => {
  const {theme} = useThemeContext();

  const users = useSelector(Selectors.users) ?? [];
  const user = users.find(u => u.id === id);

  const {data: userImg} = api.usePreviewFileQuery(user?.img ?? skipToken);

  const loggedAvatarColor =
    theme.palette.mode === "dark"
      ? theme.palette.secondary.main
      : theme.palette.secondary.dark;

  const bgAvatarColor = user
    ? getUserAvatarColor(user, theme.palette.mode === "dark")
    : loggedAvatarColor;

  const text = user && getUserText(user);

  if (full) {
    return (
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{minWidth: 0, inlineSize: "100%"}}>
        <Avatar
          onClick={onClick}
          src={userImg}
          sx={{
            bgcolor: bgAvatarColor,
            color: theme.palette.getContrastText(bgAvatarColor),
            width,
            height: width,
            ...sx,
          }}
        />
        <Typography
          color="textPrimary"
          variant={variant ?? "body2"}
          sx={{...truncateSx}}>
          {text ?? emptyLabel ?? "-"}
        </Typography>
      </Stack>
    );
  }

  return (
    <Avatar
      onClick={onClick}
      src={userImg}
      sx={{
        bgcolor: bgAvatarColor,
        color: theme.palette.getContrastText(bgAvatarColor),
        width,
        height: width,
        ...sx,
      }}>
      {text ? (
        <Tooltip title={text}>
          <Typography
            color="textPrimary"
            lineHeight={1}
            variant={variant ?? "body2"}>
            {acronym(text)}
          </Typography>
        </Tooltip>
      ) : (
        <Person sx={{width, height: width}} />
      )}
    </Avatar>
  );
};

export {UserAvatar};
